import React, { useState, useEffect, useRef } from 'react';
import { initiatePayment, checkPaymentStatus } from '../services/api';
import './PaymentModal.css';

function PaymentModal({ username, onSuccess, onClose }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [step, setStep] = useState('input'); // input | waiting | success | failed
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutRequestId, setCheckoutRequestId] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, []);

  const clearPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const handleSubmit = async () => {
    setError('');

    if (!phoneNumber || phoneNumber.trim().length < 9) {
      setError('Please enter a valid M-Pesa phone number');
      return;
    }

    setLoading(true);

    try {
      const result = await initiatePayment(username, phoneNumber);

      if (result.success) {
        setCheckoutRequestId(result.checkoutRequestId);
        setStep('waiting');
        setCountdown(60);
        startPolling(result.checkoutRequestId);
        startCountdown();
      } else {
        setError(result.message || 'Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (checkoutId) => {
    pollRef.current = setInterval(async () => {
      try {
        const status = await checkPaymentStatus(checkoutId);
        if (status.isPremium) {
          clearPolling();
          setStep('success');
          setTimeout(() => onSuccess(), 2000);
        } else if (status.status === 'FAILED') {
          clearPolling();
          setStep('failed');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000); // poll every 5 seconds
  };

  const startCountdown = () => {
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearPolling();
          setStep('failed');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRetry = () => {
    clearPolling();
    setStep('input');
    setError('');
    setCountdown(60);
    setCheckoutRequestId(null);
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>✕</button>

        {/* Step: Input phone number */}
        {step === 'input' && (
          <>
            <div className="modal-header">
              <div className="modal-icon">📱</div>
              <h2>Subscribe to Premium</h2>
              <p>KES 249/month — pay via M-Pesa</p>
            </div>

            {error && <div className="modal-error">⚠️ {error}</div>}

            <div className="modal-body">
              <label htmlFor="phone">M-Pesa Phone Number</label>
              <input
                id="phone"
                type="tel"
                placeholder="e.g. 0712345678"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                className="phone-input"
                maxLength={13}
              />
              <small className="input-hint">Enter the phone number registered with M-Pesa</small>
            </div>

            <div className="modal-footer">
              <button
                className="pay-btn"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? '⏳ Sending request...' : '💳 Pay KES 249 via M-Pesa'}
              </button>
              <button className="cancel-btn" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* Step: Waiting for payment */}
        {step === 'waiting' && (
          <>
            <div className="modal-header">
              <div className="modal-icon waiting-icon">⏳</div>
              <h2>Check Your Phone</h2>
              <p>An M-Pesa payment request has been sent to <strong>{phoneNumber}</strong></p>
            </div>
            <div className="modal-body waiting-body">
              <div className="waiting-steps">
                <div className="waiting-step">1. Open M-Pesa on your phone</div>
                <div className="waiting-step">2. Enter your M-Pesa PIN</div>
                <div className="waiting-step">3. Confirm the KES 249 payment</div>
              </div>
              <div className="countdown">
                <div className="countdown-ring">
                  <span>{countdown}s</span>
                </div>
                <p>Waiting for payment confirmation...</p>
              </div>
            </div>
            <button className="cancel-btn" onClick={handleRetry}>
              ← Try Again with Different Number
            </button>
          </>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="modal-result success-result">
            <div className="result-icon">🎉</div>
            <h2>Payment Successful!</h2>
            <p>Welcome to Couple Check Premium. Enjoy all your new features!</p>
          </div>
        )}

        {/* Step: Failed */}
        {step === 'failed' && (
          <div className="modal-result failed-result">
            <div className="result-icon">❌</div>
            <h2>Payment Not Completed</h2>
            <p>The payment was cancelled or timed out. Please try again.</p>
            <button className="pay-btn" onClick={handleRetry}>Try Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentModal;